import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "./AppSidebar";
import AttachmentsModal from "./AttachmentsModal";
import { API } from "./cache";
import { EntityTagsField, FilterTriggerButton, ChoiceFilterPopover, PageToolbarSearch } from "./features/common/components";
import { createTagMapping } from "./features/common/services/tagsApi";
import { downloadAttachment } from "./shared/utils";
import "./ProductReleasesView.css";

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconClose() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function IconPlus() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}
function IconEdit() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 2l3 3-8 8H3v-3L11 2z"/>
    </svg>
  );
}
function IconTrash() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2 4 14 4"/><path d="M5 4V2h6v2"/><path d="M3 4l1 10h8l1-10"/>
    </svg>
  );
}
function IconChevronUp() {
  return (
    <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <polyline points="1 5 5 1 9 5"/>
    </svg>
  );
}
function IconChevronDown() {
  return (
    <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <polyline points="1 1 5 5 9 1"/>
    </svg>
  );
}
function IconInfo() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6.5"/>
      <line x1="8" y1="7.5" x2="8" y2="11"/>
      <circle cx="8" cy="5.2" r="0.6" fill="currentColor" stroke="none"/>
    </svg>
  );
}
function IconPaperclip() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4.5v6a2 2 0 1 0 4 0V4.2a3.2 3.2 0 1 0-6.4 0V11a4.5 4.5 0 1 0 9 0V5.5"/>
    </svg>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS    = ["planned", "in_progress", "released", "failed", "rolled_back"];
const PLATFORM_OPTIONS  = ["Mobile", "Web", "API"];
const PLAT_TYPE_OPTIONS = ["iOS", "Android"];
const ENVIRONMENT_OPTIONS = ["Production", "UAT", "DEV", "Staging", "QA"];

const STATUS_LABEL = {
  planned:     "Planned",
  in_progress: "In Progress",
  released:    "Released",
  failed:      "Failed",
  rolled_back: "Rolled Back",
};

const STATUS_CLASS = {
  planned:     "prv-badge--planned",
  in_progress: "prv-badge--in-progress",
  released:    "prv-badge--released",
  failed:      "prv-badge--failed",
  rolled_back: "prv-badge--rolled-back",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return null;
  const s = typeof iso === "string" && !iso.includes("T") ? iso + "T00:00:00Z" : iso;
  const d = new Date(s);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fileIcon(fileType) {
  if (!fileType) return "📎";
  if (fileType.startsWith("image")) return "🖼";
  if (fileType.includes("pdf")) return "📄";
  if (fileType.includes("sheet") || fileType.includes("excel") || fileType.includes("csv")) return "📊";
  if (fileType.includes("word") || fileType.includes("doc")) return "📝";
  return "📎";
}

function toInputDate(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function platformDisplay(platform, platform_type) {
  if (platform === "Mobile" && platform_type) return `${platform} (${platform_type})`;
  return platform || "—";
}

function prvInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function prvAvatarColor(name) {
  if (!name) return 1;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return (h % 5) + 1;
}

function PersonCell({ name }) {
  if (!name) return <span className="prv-cell-text" style={{ color: "#94a3b8" }}>—</span>;
  return (
    <div className="prv-people">
      <span className={`prv-avatar prv-avatar--ac${prvAvatarColor(name)}`}>{prvInitials(name)}</span>
      <span className="prv-people-name">{name}</span>
    </div>
  );
}

function makeEmpty() {
  return {
    platform:             "Web",
    platform_type:        null,
    customer_id:          "",
    product_id:           "",
    project_id:           "",
    version_no:           "",
    environment:          "",
    build_no:             "",
    build_date:           "",
    git_commit_id:        "",
    planned_release_date: "",
    release_date:         "",
    status:               "planned",
    release_notes:        "",
    approved_by_internal: "",
    approved_by_external: "",
  };
}

// ─── ComboBox ─────────────────────────────────────────────────────────────────

function ComboBox({ options = [], value, onChange, placeholder = "Select…", error, className, disabled }) {
  const [open,        setOpen]        = useState(false);
  const [query,       setQuery]       = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => { setHighlighted(0); }, [filtered.length]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.children[highlighted]?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const openDropdown  = () => { if (disabled) return; setQuery(""); setOpen(true); };
  const closeDropdown = () => { setQuery(""); setOpen(false); };
  const commit = (opt) => { onChange(opt.value); closeDropdown(); };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { openDropdown(); return; }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) { setOpen(true); setQuery(e.key); return; }
      return;
    }
    if (e.key === "ArrowDown")  { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp")    { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter")      { e.preventDefault(); if (filtered[highlighted]) commit(filtered[highlighted]); }
    if (e.key === "Escape")     { closeDropdown(); }
    if (e.key === "Tab")        { closeDropdown(); }
  };

  return (
    <div
      ref={wrapRef}
      className={`prv-combo${open ? " prv-combo--open" : ""}${error ? " prv-combo--error" : ""}${disabled ? " prv-combo--disabled" : ""}${className ? ` ${className}` : ""}`}
    >
      <div className="prv-combo-wrap">
        <input
          ref={inputRef}
          className="prv-combo-input"
          value={open ? query : (selected?.label || "")}
          placeholder={open ? "Type to filter…" : (selected?.label ? "" : placeholder)}
          readOnly={!open}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (!open) openDropdown(); }}
          onMouseDown={(e) => { if (!open) { e.preventDefault(); openDropdown(); } }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          disabled={disabled}
        />
        <span
          className="prv-combo-arrow"
          onMouseDown={(e) => { e.preventDefault(); open ? closeDropdown() : openDropdown(); }}
        >
          <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <polyline points="1 1 5 5 9 1"/>
          </svg>
        </span>
      </div>
      {open && (
        <ul ref={listRef} className="prv-combo-list">
          {filtered.length === 0 ? (
            <li className="prv-combo-empty">No matches</li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={`${opt.value}__${i}`}
                className={`prv-combo-opt${i === highlighted ? " prv-combo-opt--hi" : ""}${opt.value === value ? " prv-combo-opt--sel" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
                onMouseEnter={() => setHighlighted(i)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// ─── Notes panel (side drawer, read-only) ────────────────────────────────────

function NotesPopup({ release, onClose, onOpenAttachments, initialTab = "overview" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [attachments, setAttachments] = useState(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState("");

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, release?.id]);

  const loadAttachments = useCallback(async () => {
    if (!release?.id) return;
    setAttachmentsLoading(true);
    setAttachmentsError("");
    try {
      const res = await fetch(`${API}/attachments/product_release/${release.id}`, { credentials: "include" });
      const data = res.ok ? await res.json() : [];
      setAttachments(Array.isArray(data) ? data : []);
    } catch {
      setAttachments([]);
      setAttachmentsError("Failed to load attachments");
    } finally {
      setAttachmentsLoading(false);
    }
  }, [release?.id]);

  useEffect(() => {
    if (activeTab === "attachments" && attachments === null) loadAttachments();
  }, [activeTab, attachments, loadAttachments]);

  const handleDownloadAttachment = async (att) => {
    try {
      await downloadAttachment(att.id);
    } catch {
      setAttachmentsError("Failed to download attachment");
    }
  };

  return (
    <>
      <div className="prv-scrim" onClick={onClose} />
      <div className="prv-drawer prv-notes-drawer" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="prv-drawer-header">
          <div className="prv-drawer-title">
            Release Notes
            {release.version_no && (
              <span className="prv-notes-title-version"> — {release.version_no}</span>
            )}
          </div>
          <button className="prv-drawer-close" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        {/* Tabs */}
        <div className="prv-drawer-body" style={{ paddingBottom: 8 }}>
          <div className="page-tabs" style={{ marginBottom: 12 }}>
            <button className={`page-tab${activeTab === "overview" ? " page-tab--active" : ""}`} onClick={() => setActiveTab("overview")}>
              Overview
            </button>
            <button className={`page-tab${activeTab === "attachments" ? " page-tab--active" : ""}`} onClick={() => setActiveTab("attachments")}>
              Attachments
              <span className="page-tab-count">{release.attachment_count || 0}</span>
            </button>
          </div>

          {activeTab === "overview" && (
            <>
              {/* Info fields */}
              <div className="prv-notes-fields">
                <div className="prv-notes-field">
                  <span className="prv-notes-field-label">Customer</span>
                  <span className="prv-notes-field-val">{release.customer_name || "—"}</span>
                </div>
                <div className="prv-notes-field">
                  <span className="prv-notes-field-label">Platform</span>
                  <span className="prv-notes-field-val">
                    {platformDisplay(release.platform, release.platform_type)}
                  </span>
                </div>
                <div className="prv-notes-field">
                  <span className="prv-notes-field-label">Version</span>
                  <span className="prv-notes-field-val prv-notes-field-mono">
                    {release.version_no || "—"}
                  </span>
                </div>
                <div className="prv-notes-field">
                  <span className="prv-notes-field-label">Planned Release</span>
                  <span className="prv-notes-field-val">
                    {fmtDate(release.planned_release_date) || "—"}
                  </span>
                </div>
                <div className="prv-notes-field">
                  <span className="prv-notes-field-label">Released On</span>
                  <span className={`prv-notes-field-val${release.release_date ? " prv-notes-field-released" : ""}`}>
                    {fmtDate(release.release_date) || "Not yet released"}
                  </span>
                </div>
                <div className="prv-notes-field">
                  <span className="prv-notes-field-label">Created</span>
                  <span className="prv-notes-field-val">
                    {fmtDateTime(release.created_at) || "—"}
                  </span>
                </div>
                <div className="prv-notes-field">
                  <span className="prv-notes-field-label">Updated</span>
                  <span className="prv-notes-field-val">
                    {fmtDateTime(release.updated_at) || "—"}
                  </span>
                </div>
              </div>

              <div className="prv-notes-section-label">Release Notes</div>
              <div className="prv-notes-content">
                {release.release_notes
                  ? release.release_notes
                  : <span className="prv-notes-empty">No release notes have been added for this release.</span>
                }
              </div>
            </>
          )}

          {activeTab === "attachments" && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <button
                  type="button"
                  className="prv-btn-cancel"
                  onClick={() => onOpenAttachments?.(release)}
                  style={{ padding: "6px 10px", fontSize: 12 }}
                >
                  Manage Attachments
                </button>
              </div>
              {attachmentsError && (
                <div className="prv-notes-empty" style={{ color: "#b91c1c", marginBottom: 8 }}>{attachmentsError}</div>
              )}
              {attachmentsLoading ? (
                <div className="prv-notes-empty">Loading attachments…</div>
              ) : !attachments || attachments.length === 0 ? (
                <div className="prv-notes-empty">No attachments yet.</div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {attachments.map((a) => (
                    <div key={a.id} style={{ display: "grid", gridTemplateColumns: "20px 1fr", gap: 8, alignItems: "start", fontSize: 13 }}>
                      <span>{fileIcon(a.file_type)}</span>
                      <div>
                        <button type="button" onClick={() => handleDownloadAttachment(a)} style={{ background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", color: "#1d4ed8", textDecoration: "none", fontWeight: 600, cursor: "pointer" }}>
                          {a.file_name}
                        </button>
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>
                          {a.caption || "—"} {a.created_at ? `· ${fmtDate(a.created_at)}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  return (
    <span className={`prv-badge ${STATUS_CLASS[status] || ""}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ─── Release drawer ───────────────────────────────────────────────────────────

function ReleaseDrawer({ release, customers, products, projects, users, onClose, onSave, onDelete, onOpenAttachments }) {
  const isEdit = !!release?.id;
  const [activeTab, setActiveTab] = useState("details");
  const [pendingTagIds, setPendingTagIds] = useState([]);
  const [attachments, setAttachments] = useState(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState("");
  const [form, setForm] = useState(
    isEdit
      ? {
          ...release,
          planned_release_date: toInputDate(release.planned_release_date),
          release_date:         toInputDate(release.release_date),
          build_date:           toInputDate(release.build_date),
          approved_by_internal: release.approved_by_internal || "",
          approved_by_external: release.approved_by_external || "",
          release_notes:        release.release_notes        || "",
          build_no:             release.build_no             || "",
          git_commit_id:        release.git_commit_id        || "",
          version_no:           release.version_no           || "",
          environment:          release.environment          || "",
          platform_type:        release.platform_type        || null,
        }
      : makeEmpty()
  );
  const [errors, setSaveErrors] = useState({});
  const [saving, setSaving]     = useState(false);

  const loadAttachments = useCallback(async () => {
    if (!release?.id) return;
    setAttachmentsLoading(true);
    setAttachmentsError("");
    try {
      const res = await fetch(`${API}/attachments/product_release/${release.id}`, { credentials: "include" });
      const data = res.ok ? await res.json() : [];
      setAttachments(Array.isArray(data) ? data : []);
    } catch {
      setAttachments([]);
      setAttachmentsError("Failed to load attachments");
    } finally {
      setAttachmentsLoading(false);
    }
  }, [release?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleDownloadAttachment = async (att) => {
    try {
      await downloadAttachment(att.id);
    } catch {
      setAttachmentsError("Failed to download attachment");
    }
  };

  const handlePlatformChange = (v) => {
    setForm((f) => ({ ...f, platform: v, platform_type: v === "Mobile" ? (f.platform_type || "") : null }));
  };

  // Projects for selected customer
  const filteredProjects = useMemo(() => {
    return projects;
  }, [projects]);

  const customerNameById = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.customer_id, c.customer_name])),
    [customers]
  );

  const validate = () => {
    const e = {};
    const isReleased = form.status === "released";
    const envNorm = (form.environment || "").trim().toLowerCase();
    const requiresExternalApproval = envNorm === "production" || envNorm === "staging";
    const selectedProject = projects.find((p) => p.project_id === form.project_id);

    if (!form.platform)                     e.platform = "Required";
    if (!form.product_id)                   e.product_id = "Required";
    if (!form.project_id)                   e.project_id = "Required";
    if (!selectedProject && form.project_id) e.project_id = "Invalid project selection";
    if (!form.planned_release_date)         e.planned_release_date = "Required";
    if ((form.environment || "").trim().length > 10) e.environment = "Max 10 characters";
    if (form.platform === "Mobile" && !form.platform_type) e.platform_type = "Required for Mobile";
    if (form.release_date && form.planned_release_date && form.release_date < form.planned_release_date) {
      e.release_date = "Must be ≥ Planned Release Date";
    }

    if (isReleased) {
      if (!form.environment?.trim())   e.environment = "Required when status is Released";
      if (!form.build_no?.trim())      e.build_no = "Required when status is Released";
      if (!form.build_date)            e.build_date = "Required when status is Released";
      if (!form.git_commit_id?.trim()) e.git_commit_id = "Required when status is Released";
      if (!form.release_date)          e.release_date = "Required when status is Released";
      if (!form.approved_by_internal)  e.approved_by_internal = "Required when status is Released";
      if (requiresExternalApproval && !form.approved_by_external?.trim()) {
        e.approved_by_external = "Required for Production/Staging when status is Released";
      }
      if (!form.release_notes?.trim()) e.release_notes = "Required when status is Released";
    }

    setSaveErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const selectedProject = projects.find((p) => p.project_id === form.project_id);
    const payload = {
      ...form,
      platform_type:        form.platform_type        || null,
      customer_id:          selectedProject?.customer_id || form.customer_id || null,
      product_id:           form.product_id           || null,
      project_id:           form.project_id           || null,
      environment:          (form.environment || "").trim() || null,
      approved_by_internal: form.approved_by_internal || null,
      planned_release_date: form.planned_release_date || null,
      release_date:         form.release_date         || null,
      build_date:           form.build_date           || null,
      build_no:             form.build_no             || null,
      git_commit_id:        form.git_commit_id        || null,
      release_notes:        form.release_notes        || null,
      approved_by_external: form.approved_by_external || null,
    };
    if (!isEdit && pendingTagIds.length > 0) {
      payload.__pending_tag_ids = pendingTagIds;
    }
    await onSave(payload);
    setSaving(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSave();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setActiveTab("details");
    setAttachments(null);
    setAttachmentsError("");
    setPendingTagIds([]);
  }, [release?.id]);

  useEffect(() => {
    if (isEdit && activeTab === "attachments" && attachments === null) {
      loadAttachments();
    }
  }, [isEdit, activeTab, attachments, loadAttachments]);

  return (
    <>
      <div className="prv-scrim" onClick={onClose} />
      <div className="prv-drawer" role="dialog" aria-modal="true">

        <div className="prv-drawer-header">
          <div className="prv-drawer-title">
            {isEdit
              ? [
                  "Edit",
                  release.customer_name,
                  platformDisplay(release.platform, release.platform_type),
                  release.version_no,
                ].filter(Boolean).join(" — ")
              : "New Release"}
          </div>
          <button className="prv-drawer-close" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="prv-drawer-body">
          <div className="page-tabs" style={{ marginBottom: 12 }}>
            <button
              className={`page-tab${activeTab === "details" ? " page-tab--active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              Details
            </button>
            {isEdit && (
              <button
                className={`page-tab${activeTab === "attachments" ? " page-tab--active" : ""}`}
                onClick={() => setActiveTab("attachments")}
              >
                Attachments
                <span className="page-tab-count">{release?.attachment_count || 0}</span>
              </button>
            )}
          </div>

          {(!isEdit || activeTab === "details") && (
          <div className="prv-form-grid">

            {/* ── Platform toggle ── */}
            <div className="prv-form-field">
              <label className="prv-form-label prv-form-label--req">Platform</label>
              <div className="prv-seg">
                {PLATFORM_OPTIONS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`prv-seg-btn${form.platform === p ? " prv-seg-btn--active" : ""}`}
                    onClick={() => handlePlatformChange(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
              {errors.platform && <span className="prv-form-error">{errors.platform}</span>}
            </div>

            {/* ── Platform type (Mobile only) ── */}
            {form.platform === "Mobile" && (
              <div className="prv-form-field">
                <label className={`prv-form-label prv-form-label--req${errors.platform_type ? " prv-form-label--err" : ""}`}>
                  Platform Type
                </label>
                <div className="prv-seg">
                  {PLAT_TYPE_OPTIONS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`prv-seg-btn${form.platform_type === t ? " prv-seg-btn--active" : ""}${errors.platform_type ? " prv-seg-btn--error" : ""}`}
                      onClick={() => set("platform_type", t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {errors.platform_type && <span className="prv-form-error">{errors.platform_type}</span>}
              </div>
            )}

            {/* ── Product ── */}
            <div className="prv-form-field">
              <label className="prv-form-label prv-form-label--req">Product</label>
              <ComboBox
                options={products.map((p) => ({ value: p.id, label: p.name }))}
                value={form.product_id}
                onChange={(v) => set("product_id", v)}
                placeholder="Search product…"
                error={!!errors.product_id}
              />
              {errors.product_id && <span className="prv-form-error">{errors.product_id}</span>}
            </div>

            {/* ── Project ── */}
            <div className="prv-form-field">
              <label className="prv-form-label prv-form-label--req">Project</label>
              <ComboBox
                options={filteredProjects.map((p) => ({
                  value: p.project_id,
                  label: customerNameById[p.customer_id]
                    ? `${p.project_name} (${customerNameById[p.customer_id]})`
                    : p.project_name,
                }))}
                value={form.project_id}
                onChange={(v) => {
                  const selected = projects.find((p) => p.project_id === v);
                  setForm((f) => ({
                    ...f,
                    project_id: v,
                    customer_id: selected?.customer_id || f.customer_id || "",
                  }));
                }}
                placeholder="Search project…"
                error={!!errors.project_id}
              />
              {errors.project_id && <span className="prv-form-error">{errors.project_id}</span>}
            </div>

            {/* ── Version No ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Version No</label>
              <input
                className={`prv-form-input${errors.version_no ? " prv-input--error" : ""}`}
                value={form.version_no}
                onChange={(e) => set("version_no", e.target.value)}
                placeholder="e.g. 2.1.0"
                autoFocus={!isEdit}
              />
              {errors.version_no && <span className="prv-form-error">{errors.version_no}</span>}
            </div>

            {/* ── Environment ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Environment</label>
              <input
                className={`prv-form-input${errors.environment ? " prv-input--error" : ""}`}
                value={form.environment || ""}
                onChange={(e) => set("environment", e.target.value.slice(0, 10))}
                placeholder="Production / UAT / DEV"
                maxLength={10}
                list="prv-environment-options"
              />
              <datalist id="prv-environment-options">
                {ENVIRONMENT_OPTIONS.map((env) => (
                  <option key={env} value={env} />
                ))}
              </datalist>
              {errors.environment && <span className="prv-form-error">{errors.environment}</span>}
            </div>

            {/* ── Build No ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Build No</label>
              <input
                className="prv-form-input"
                value={form.build_no}
                onChange={(e) => set("build_no", e.target.value)}
                placeholder="e.g. 1042"
              />
              {errors.build_no && <span className="prv-form-error">{errors.build_no}</span>}
            </div>

            {/* ── Build Date ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Build Date</label>
              <input
                type="date"
                className={`prv-form-input${errors.build_date ? " prv-input--error" : ""}`}
                value={form.build_date}
                onChange={(e) => set("build_date", e.target.value)}
              />
              {errors.build_date && <span className="prv-form-error">{errors.build_date}</span>}
            </div>

            {/* ── Git Commit ID ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Git Commit ID</label>
              <input
                className={`prv-form-input prv-mono-input${errors.git_commit_id ? " prv-input--error" : ""}`}
                value={form.git_commit_id}
                onChange={(e) => set("git_commit_id", e.target.value)}
                placeholder="e.g. a3f2b91c"
              />
              {errors.git_commit_id && <span className="prv-form-error">{errors.git_commit_id}</span>}
            </div>

            {/* ── Planned Release Date ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Planned Release Date</label>
              <input
                type="date"
                className={`prv-form-input${errors.planned_release_date ? " prv-input--error" : ""}`}
                value={form.planned_release_date}
                onChange={(e) => set("planned_release_date", e.target.value)}
              />
              {errors.planned_release_date && <span className="prv-form-error">{errors.planned_release_date}</span>}
            </div>

            {/* ── Release Date ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Release Date</label>
              <input
                type="date"
                className={`prv-form-input${errors.release_date ? " prv-input--error" : ""}`}
                value={form.release_date}
                onChange={(e) => set("release_date", e.target.value)}
              />
              {errors.release_date && <span className="prv-form-error">{errors.release_date}</span>}
            </div>

            {/* ── Status (edit only) ── */}
            {isEdit && (
              <div className="prv-form-field">
                <label className="prv-form-label">Status</label>
                <select
                  className="prv-form-select"
                  value={form.status}
                  onChange={(e) => set("status", e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </div>
            )}

            {/* ── Approved By Internal ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Approved By (Internal)</label>
              <ComboBox
                options={users.map((u) => ({ value: u.id, label: u.display_name || u.user_name }))}
                value={form.approved_by_internal}
                onChange={(v) => set("approved_by_internal", v)}
                placeholder="Search user…"
                error={!!errors.approved_by_internal}
              />
              {errors.approved_by_internal && <span className="prv-form-error">{errors.approved_by_internal}</span>}
            </div>

            {/* ── Approved By External ── */}
            <div className="prv-form-field">
              <label className="prv-form-label">Approved By (External)</label>
              <input
                className={`prv-form-input${errors.approved_by_external ? " prv-input--error" : ""}`}
                value={form.approved_by_external}
                onChange={(e) => set("approved_by_external", e.target.value)}
                placeholder="Name or email of external approver"
              />
              {errors.approved_by_external && <span className="prv-form-error">{errors.approved_by_external}</span>}
            </div>

            {/* ── Release Notes ── */}
            <div className="prv-form-field prv-form-field--full">
              <label className="prv-form-label">Release Notes</label>
              <textarea
                className={`prv-form-textarea${errors.release_notes ? " prv-input--error" : ""}`}
                value={form.release_notes}
                onChange={(e) => set("release_notes", e.target.value)}
                placeholder="Summary of changes, known issues, deployment steps…"
                rows={4}
              />
              {errors.release_notes && <span className="prv-form-error">{errors.release_notes}</span>}
            </div>

            <div className="prv-form-field prv-form-field--full">
              <EntityTagsField
                moduleName="releases"
                moduleScope="releases"
                entityId={isEdit ? release?.id : null}
                canEdit={true}
                title="Tags"
                draftTagIds={pendingTagIds}
                onDraftTagIdsChange={setPendingTagIds}
              />
            </div>

          </div>
          )}

          {isEdit && activeTab === "attachments" && (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="prv-btn-cancel"
                  onClick={() => onOpenAttachments?.(release)}
                  style={{ padding: "6px 10px", fontSize: 12 }}
                >
                  Manage Attachments
                </button>
              </div>
              {attachmentsError && (
                <div className="prv-notes-empty" style={{ color: "#b91c1c" }}>{attachmentsError}</div>
              )}
              {attachmentsLoading ? (
                <div className="prv-notes-empty">Loading attachments…</div>
              ) : !attachments || attachments.length === 0 ? (
                <div className="prv-notes-empty">No attachments yet.</div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {attachments.map((a) => (
                    <div key={a.id} style={{ display: "grid", gridTemplateColumns: "20px 1fr", gap: 8, alignItems: "start", fontSize: 13 }}>
                      <span>{fileIcon(a.file_type)}</span>
                      <div>
                        <button type="button" onClick={() => handleDownloadAttachment(a)} style={{ background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", color: "#1d4ed8", textDecoration: "none", fontWeight: 600, cursor: "pointer" }}>
                          {a.file_name}
                        </button>
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>
                          {a.caption || "—"} {a.created_at ? `· ${fmtDate(a.created_at)}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        <div className="prv-drawer-footer">
          {isEdit && (
            <button className="prv-btn-delete" onClick={() => onDelete(release)} disabled={saving}>
              Delete
            </button>
          )}
          <div className="prv-drawer-spacer" />
          <button className="prv-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="prv-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create release"}
            {!saving && (
              <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 3, fontFamily: "monospace" }}>⌘↵</span>
            )}
          </button>
        </div>

      </div>
    </>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function ProductReleasesView({ user }) {
  const userType = String(user?.db_user_type || "").toLowerCase();
  const isExternalUser = userType === "external" || userType === "external user";
  const userRole = String(user?.db_role || "").toLowerCase();
  const canCreate = userRole === "admin" || userRole === "super admin" || userRole === "am";
  const [releases,         setReleases]         = useState([]);
  const [customers,        setCustomers]        = useState([]);
  const [products,         setProducts]         = useState([]);
  const [projects,         setProjects]         = useState([]);
  const [users,            setUsers]            = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [tab,              setTab]              = useState(isExternalUser ? "all" : "upcoming");
  const [search,           setSearch]           = useState("");
  const [sortKey,          setSortKey]          = useState("planned_release_date");
  const [sortDir,          setSortDir]          = useState("asc");
  const [page,             setPage]             = useState(1);
  const [rowsPerPage,      setRowsPerPage]      = useState(50);
  const [drawer,           setDrawer]           = useState(null);
  const [confirmDelete,    setConfirmDelete]    = useState(null);
  const [notesPopup,       setNotesPopup]       = useState(null); // { release, initialTab }
  const [attachmentsPopup, setAttachmentsPopup] = useState(null); // { release }
  const [toasts,           setToasts]           = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filterOpen,     setFilterOpen]     = useState(false);
  const searchRef = useRef(null);

  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [relRes, custRes, prodRes, usersRes] = await Promise.all([
        fetch(`${API}/product-releases?limit=1000`, { credentials: "include" }),
        fetch(
          `${API}/customers-projects?active_only=true${user?.db_user_id ? `&user_id=${encodeURIComponent(user.db_user_id)}` : ""}`,
          { credentials: "include" }
        ),
        fetch(`${API}/products`,                     { credentials: "include" }),
        fetch(`${API}/users/dropdown`,               { credentials: "include" }),
      ]);
      const [relData, custData, prodData, usersData] = await Promise.all([
        relRes.ok   ? relRes.json()   : [],
        custRes.ok  ? custRes.json()  : [],
        prodRes.ok  ? prodRes.json()  : [],
        usersRes.ok ? usersRes.json() : [],
      ]);
      setReleases(relData);
      setCustomers(custData);
      setProducts(prodData);
      setUsers(usersData);
      // Flatten projects from all customers
      const flat = [];
      for (const c of custData) {
        for (const p of (c.projects || [])) {
          flat.push({ ...p, customer_id: c.customer_id });
        }
      }
      setProjects(flat);
    } catch {
      addToast("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  }, [addToast, user?.db_user_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const inInput = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (canCreate && !inInput && e.key === "n" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setDrawer({ mode: "new" });
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isExternalUser]);

  // Tab default sort: released tab → release_date DESC, upcoming → planned_release_date ASC
  const handleTabChange = (newTab) => {
    setTab(newTab);
    setPage(1);
    if (newTab === "released") {
      setSortKey("release_date");
      setSortDir("desc");
    } else if (newTab === "upcoming") {
      setSortKey("planned_release_date");
      setSortDir("asc");
    }
  };

  const filterFields = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
    return [
      {
        key: "status",
        label: "Status",
        options: STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
      },
      {
        key: "platform",
        label: "Platform",
        options: PLATFORM_OPTIONS.map((p) => ({ value: p, label: p })),
      },
      {
        key: "environment",
        label: "Environment",
        options: uniq(releases.map((r) => r.environment)).map((e) => ({ value: e, label: e })),
      },
      {
        key: "customer_name",
        label: "Customer",
        options: uniq(releases.map((r) => r.customer_name)).map((n) => ({ value: n, label: n })),
      },
      {
        key: "product_name",
        label: "Product",
        options: uniq(releases.map((r) => r.product_name)).map((n) => ({ value: n, label: n })),
      },
      { key: "planned_release_date", label: "Planned Date",  type: "daterange" },
      { key: "release_date",         label: "Release Date",  type: "daterange" },
      { key: "created_at",           label: "Created Date",  type: "daterange" },
      { key: "updated_at",           label: "Updated Date",  type: "daterange" },
    ];
  }, [releases]);

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter(
      (f) => f && (f.type === "daterange" ? !!(f.from || f.to) : Array.isArray(f.values) && f.values.length > 0)
    ).length,
    [appliedFilters]
  );

  const filteredReleases = useMemo(() => {
    let list = releases;

    if (tab === "released") {
      list = list.filter((r) => r.status === "released");
    } else if (tab === "upcoming") {
      list = list.filter((r) => r.status === "planned" || r.status === "in_progress");
    }

    for (const [key, filterDef] of Object.entries(appliedFilters)) {
      if (!filterDef) continue;
      if (filterDef.type === "daterange") {
        const { from, to } = filterDef;
        if (from) list = list.filter((r) => r[key] && String(r[key]).slice(0, 10) >= from);
        if (to)   list = list.filter((r) => r[key] && String(r[key]).slice(0, 10) <= to);
        continue;
      }
      if (!Array.isArray(filterDef.values) || filterDef.values.length === 0) continue;
      const vals = new Set(filterDef.values.map((v) => String(v).toLowerCase()));
      list = list.filter((r) => {
        const rv = String(r[key] || "").toLowerCase();
        return filterDef.op === "is_not" ? !vals.has(rv) : vals.has(rv);
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.version_no    || "").toLowerCase().includes(q) ||
        (r.customer_name || "").toLowerCase().includes(q) ||
        (r.product_name  || "").toLowerCase().includes(q) ||
        (r.project_name  || "").toLowerCase().includes(q) ||
        (r.platform      || "").toLowerCase().includes(q) ||
        (r.environment   || "").toLowerCase().includes(q) ||
        (r.build_no      || "").toLowerCase().includes(q)
      );
    }

    list = [...list].sort((a, b) => {
      if (sortKey === "attachment_count") {
        const avNum = Number(a.attachment_count || 0);
        const bvNum = Number(b.attachment_count || 0);
        return sortDir === "asc" ? avNum - bvNum : bvNum - avNum;
      }
      const av = (a[sortKey] ?? "").toString();
      const bv = (b[sortKey] ?? "").toString();
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });

    return list;
  }, [releases, tab, search, sortKey, sortDir, appliedFilters]);

  const tabCounts = useMemo(() => ({
    all:      releases.length,
    released: releases.filter((r) => r.status === "released").length,
    upcoming: releases.filter((r) => r.status === "planned" || r.status === "in_progress").length,
  }), [releases]);

  const pageCount = Math.max(1, Math.ceil(filteredReleases.length / rowsPerPage));
  const pageRows  = filteredReleases.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
    setPage(1);
  };

  const handleSave = async (form) => {
    const pendingTagIds = Array.isArray(form?.__pending_tag_ids) ? form.__pending_tag_ids : [];
    const payload = { ...form };
    delete payload.__pending_tag_ids;

    if (form.id) {
      try {
        const res = await fetch(`${API}/product-releases/${form.id}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          addToast(err.detail || "Failed to save changes", "error");
          return;
        }
        setDrawer(null);
        addToast("Changes saved");
        await loadData();
      } catch {
        addToast("Failed to save changes", "error");
      }
    } else {
      try {
        const res = await fetch(`${API}/product-releases`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          addToast(err.detail || "Failed to create release", "error");
          return;
        }
        const created = await res.json().catch(() => null);
        const createdReleaseId = created?.id;
        if (createdReleaseId && pendingTagIds.length > 0) {
          const mapResults = await Promise.allSettled(
            pendingTagIds.map((tagId) =>
              createTagMapping({
                module_name: "releases",
                entity_id: createdReleaseId,
                tag_id: tagId,
              })
            )
          );
          const failed = mapResults.filter((r) => r.status === "rejected").length;
          if (failed > 0) {
            addToast(`Release created, but ${failed} tag mapping(s) failed`, "error");
          }
        }
        setDrawer(null);
        addToast("Release created");
        await loadData();
      } catch {
        addToast("Failed to create release", "error");
      }
    }
  };

  const handleDelete = async (release) => {
    setConfirmDelete(null);
    setDrawer(null);
    try {
      const res = await fetch(`${API}/product-releases/${release.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) { addToast("Failed to delete release", "error"); return; }
      setReleases((prev) => prev.filter((r) => r.id !== release.id));
      addToast("Release deleted");
    } catch {
      addToast("Failed to delete release", "error");
    }
  };

  const COLS = [
    { key: "platform",             label: "Platform",   width: 150, sortable: true, sticky: "col1" },
    { key: "customer_name",        label: "Customer",   width: 170, sortable: true, sticky: "col2" },
    { key: "project_name",         label: "Project",    width: 170, sortable: true, sticky: "col3" },
    { key: "product_name",         label: "Product",    width: 160, sortable: true },
    { key: "environment",          label: "Env",        width: 95,  sortable: true },
    { key: "version_no",           label: "Version",    width: 120, sortable: true },
    { key: "build_no",             label: "Build",      width: 100, sortable: true },
    { key: "status",               label: "Status",     width: 120, sortable: true },
    { key: "attachment_count",     label: "Files",      width: 80,  sortable: true },
    { key: "build_date",           label: "Build Date", width: 110, sortable: true },
    { key: "planned_release_date", label: "Planned",    width: 110, sortable: true },
    { key: "release_date",         label: "Released",   width: 110, sortable: true },
    { key: "created_by_name",           label: "Created By",          width: 140, sortable: true },
    { key: "approved_by_internal_name", label: "Approved (Internal)", width: 150, sortable: true },
    { key: "approved_by_external",      label: "Approved (External)", width: 150, sortable: true },
    { key: "created_at",                label: "Created",             width: 150, sortable: true },
    { key: "updated_at",                label: "Updated",             width: 150, sortable: true },
  ];

  return (
    <div
      className="prv-root"
      style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed((c) => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="prv-main">

        {/* ── Header ── */}
        <div className="page-head">
          <h1>
            Product Releases
            {" "}
            <em>{filteredReleases.length} of {releases.length}</em>
          </h1>
          <div className="page-head-actions">
            {canCreate && (
              <button className="prv-new-btn" onClick={() => setDrawer({ mode: "new" })}>
                <IconPlus />
                New release
                <span className="mv2-kbd">N</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="page-toolbar">
          <div className="page-tabs">
            {[
              { id: "all",      label: "All"      },
              { id: "released", label: "Released" },
              { id: "upcoming", label: "Upcoming" },
            ].map(({ id, label }) => (
              <button
                key={id}
                className={`page-tab${tab === id ? " page-tab--active" : ""}`}
                onClick={() => handleTabChange(id)}
              >
                {label}
                <span className="page-tab-count">{tabCounts[id]}</span>
              </button>
            ))}
          </div>

          <div className="page-toolbar-right">
            <PageToolbarSearch
              inputRef={searchRef}
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder="Search releases…"
            />
            <FilterTriggerButton
              activeCount={activeFilterCount}
              onClick={() => setFilterOpen(true)}
              onClear={() => { setAppliedFilters({}); setPage(1); }}
            />
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="prv-loading">
            <div className="prv-spinner" />
            Loading releases…
          </div>
        ) : (
          <>
            <div className="page-table-wrap">
              <table className="page-table">
                <thead>
                  <tr>
                    {COLS.map((col) => (
                      <th
                        key={col.key}
                        style={{ width: col.width }}
                        className={`${
                          col.sortable ? "page-th-sortable" : ""
                        }${sortKey === col.key ? " page-th--sorted" : ""}${
                          col.sticky ? ` prv-sticky-${col.sticky}` : ""
                        }`}
                        onClick={() => col.sortable && handleSort(col.key)}
                      >
                        <div className="page-th-inner">
                          {col.label}
                          {col.sortable && (
                            <span className={`page-sort-icon${sortKey === col.key ? " page-sort-icon--active" : ""}`}>
                              {sortKey === col.key && sortDir === "asc" ? <IconChevronUp /> : <IconChevronDown />}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    {/* Actions column */}
                    <th style={{ width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={COLS.length + 1}>
                        <div className="prv-empty">
                          <svg viewBox="0 0 64 64" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="10" y="10" width="44" height="44" rx="6"/>
                            <line x1="20" y1="24" x2="44" y2="24"/>
                            <line x1="20" y1="32" x2="44" y2="32"/>
                            <line x1="20" y1="40" x2="36" y2="40"/>
                          </svg>
                          <div className="prv-empty-title">No releases found</div>
                          <div className="prv-empty-sub">
                            {search ? "Try adjusting your search" : "Create your first release to get started"}
                          </div>
                          {!search && canCreate && (
                            <button className="prv-empty-btn" onClick={() => setDrawer({ mode: "new" })}>
                              New release
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((r) => (
                      <tr key={r.id} className="prv-row">

                        {/* Platform */}
                        <td
                          style={{ width: 150 }}
                          className="prv-sticky-col1 prv-cell-open-edit"
                          onClick={() => setDrawer({ mode: "edit", release: r })}
                          title="Open edit view"
                        >
                          <span className="prv-platform-cell">{platformDisplay(r.platform, r.platform_type)}</span>
                        </td>

                        {/* Customer */}
                        <td
                          style={{ width: 170 }}
                          className="prv-sticky-col2 prv-cell-open-edit"
                          onClick={() => setDrawer({ mode: "edit", release: r })}
                          title="Open edit view"
                        >
                          <span className="prv-cell-text" title={r.customer_name}>{r.customer_name || "—"}</span>
                        </td>

                        {/* Project */}
                        <td
                          style={{ width: 170 }}
                          className="prv-sticky-col3 prv-cell-open-edit"
                          onClick={() => setDrawer({ mode: "edit", release: r })}
                          title="Open edit view"
                        >
                          <span className="prv-cell-text" title={r.project_name}>{r.project_name || "—"}</span>
                        </td>

                        {/* Product */}
                        <td style={{ width: 160 }}>
                          <span className="prv-cell-text" title={r.product_name}>{r.product_name || "—"}</span>
                        </td>

                        {/* Environment */}
                        <td style={{ width: 95 }}>
                          <span className="prv-cell-text" title={r.environment}>{r.environment || "—"}</span>
                        </td>

                        {/* Version */}
                        <td style={{ width: 120 }}>
                          <span className="prv-version">{r.version_no || "—"}</span>
                        </td>

                        {/* Build */}
                        <td style={{ width: 100 }}>
                          <span className="prv-build">{r.build_no || "—"}</span>
                        </td>

                        {/* Status */}
                        <td style={{ width: 120 }}>
                          <StatusBadge status={r.status} />
                        </td>

                        {/* Files */}
                        <td style={{ width: 80, textAlign: "center" }}>
                          {(r.attachment_count || 0) > 0 ? (
                            <button
                              type="button"
                              className="prv-row-btn"
                              title={`${r.attachment_count} attachment${r.attachment_count > 1 ? "s" : ""}`}
                              onClick={() => setNotesPopup({ release: r, initialTab: "attachments" })}
                            >
                              <IconPaperclip />
                              <span style={{ marginLeft: 4, fontSize: 11 }}>{r.attachment_count}</span>
                            </button>
                          ) : (
                            <span className="prv-cell-text" style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>

                        {/* Build Date */}
                        <td style={{ width: 110 }}>
                          <span className="prv-date-cell">{fmtDate(r.build_date) || "—"}</span>
                        </td>

                        {/* Planned Date */}
                        <td style={{ width: 110 }}>
                          <span className="prv-date-cell">{fmtDate(r.planned_release_date) || "—"}</span>
                        </td>

                        {/* Released */}
                        <td style={{ width: 110 }}>
                          <span className="prv-date-cell">{fmtDate(r.release_date) || "—"}</span>
                        </td>

                        {/* Created By */}
                        <td style={{ width: 140 }}>
                          <PersonCell name={r.created_by_name} />
                        </td>

                        {/* Approved (Internal) */}
                        <td style={{ width: 150 }}>
                          <PersonCell name={r.approved_by_internal_name} />
                        </td>

                        {/* Approved (External) */}
                        <td style={{ width: 150 }}>
                          <PersonCell name={r.approved_by_external} />
                        </td>

                        {/* Created */}
                        <td style={{ width: 150 }}>
                          <span className="prv-date-cell">{fmtDateTime(r.created_at) || "—"}</span>
                        </td>

                        {/* Updated */}
                        <td style={{ width: 150 }}>
                          <span className="prv-date-cell">{fmtDateTime(r.updated_at) || "—"}</span>
                        </td>

                        {/* Row actions */}
                        <td style={{ width: 90 }}>
                          <div className="prv-row-actions">
                            <button
                              className="prv-row-btn prv-row-btn--info"
                              title="Release notes"
                              onClick={() => {
                                setNotesPopup((prev) =>
                                  prev?.release.id === r.id ? null : { release: r, initialTab: "overview" }
                                );
                              }}
                            >
                              <IconInfo />
                            </button>
                            <button
                              className="prv-row-btn"
                              title="Edit"
                              onClick={() => setDrawer({ mode: "edit", release: r })}
                            >
                              <IconEdit />
                            </button>
                            <button
                              className="prv-row-btn prv-row-btn--danger"
                              title="Delete"
                              onClick={() => setConfirmDelete(r)}
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Table footer / pagination */}
            {filteredReleases.length > 0 && (
              <div className="prv-table-footer">
                <span className="prv-footer-info">
                  Showing {Math.min((page - 1) * rowsPerPage + 1, filteredReleases.length)}–
                  {Math.min(page * rowsPerPage, filteredReleases.length)} of {filteredReleases.length} releases
                </span>
                <div className="prv-footer-rpp">
                  <span>Rows per page</span>
                  <select
                    className="prv-footer-select"
                    value={rowsPerPage}
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                  >
                    {[25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div className="prv-pagination">
                  <button className="prv-page-btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                  <button className="prv-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)} title="Prev">‹</button>
                  <span className="prv-page-info">{page} / {pageCount}</span>
                  <button className="prv-page-btn" disabled={page === pageCount} onClick={() => setPage((p) => p + 1)} title="Next">›</button>
                  <button className="prv-page-btn" disabled={page === pageCount} onClick={() => setPage(pageCount)} title="Last">»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Drawer ── */}
      {drawer && (
        <ReleaseDrawer
          release={drawer.mode === "edit" ? drawer.release : null}
          customers={customers}
          products={products}
          projects={projects}
          users={users}
          onClose={() => setDrawer(null)}
          onSave={handleSave}
          onDelete={(r) => { setDrawer(null); setConfirmDelete(r); }}
          onOpenAttachments={(release) => setAttachmentsPopup({ release })}
        />
      )}

      {/* ── Confirm delete ── */}
      {confirmDelete && (
        <div className="prv-confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="prv-confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="prv-confirm-msg">
              Delete release{" "}
              <strong>{confirmDelete.version_no}</strong>
              {confirmDelete.customer_name ? ` for ${confirmDelete.customer_name}` : ""}?
            </div>
            <div className="prv-confirm-actions">
              <button className="prv-btn-delete" onClick={() => handleDelete(confirmDelete)}>Delete</button>
              <button className="prv-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Notes popup ── */}
      {notesPopup && (
        <NotesPopup
          release={notesPopup.release}
          initialTab={notesPopup.initialTab || "overview"}
          onClose={() => setNotesPopup(null)}
          onOpenAttachments={(release) => setAttachmentsPopup({ release })}
        />
      )}

      {attachmentsPopup?.release?.id && (
        <AttachmentsModal
          refType="product_release"
          refId={attachmentsPopup.release.id}
          title={`Release Attachments${attachmentsPopup.release.version_no ? ` — ${attachmentsPopup.release.version_no}` : ""}`}
          defaultCaption="Releases"
          lockCaption
          maxFileSizeBytes={200 * 1024 * 1024}
          onClose={() => { setAttachmentsPopup(null); loadData(); }}
        />
      )}

      {/* ── Filter drawer ── */}
      <ChoiceFilterPopover
        open={filterOpen}
        title="Filter Releases"
        fields={filterFields}
        appliedFilters={appliedFilters}
        onApply={(filters) => { setAppliedFilters(filters); setPage(1); }}
        onClose={() => setFilterOpen(false)}
      />

      {/* ── Toasts ── */}
      <div className="prv-toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`prv-toast${t.type === "error" ? " prv-toast--error" : ""}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}
